import { DATE_FORMAT } from "./constants";

// Acceptable Quality Level (II). Lot size is the inspection qty. Sample size
// is the same for AQL 1.0, 1.5, and 2.5; those columns are accept limits.
const AQL_SAMPLE_SIZES = [
  { from: 2, to: 8, sample: 2 },
  { from: 9, to: 15, sample: 3 },
  { from: 16, to: 25, sample: 5 },
  { from: 26, to: 50, sample: 8 },
  { from: 51, to: 90, sample: 13 },
  { from: 91, to: 150, sample: 20 },
  { from: 151, to: 280, sample: 32 },
  { from: 281, to: 500, sample: 50 },
  { from: 501, to: 1200, sample: 80 },
  { from: 1201, to: 3200, sample: 125 },
  { from: 3201, to: 10000, sample: 200 },
  { from: 10001, to: 35000, sample: 315 },
  { from: 35001, to: 150000, sample: 500 },
  { from: 150001, to: 500000, sample: 800 },
  { from: 500001, to: Infinity, sample: 1250 },
];

export const sampleQtyForInspectionQty = (inspectionQty) => {
  const lot = Number(inspectionQty);
  if (!Number.isFinite(lot)) return null;
  const row = AQL_SAMPLE_SIZES.find((range) => lot >= range.from && lot <= range.to);
  return row ? row.sample : null;
};

const toNumber = (value) => (value === null || value === undefined || value === "" ? 0 : Number(value));

const formatDate = (value) => (value ? value.format(DATE_FORMAT) : null);

export const sumDefectBreakdown = (rows) => (rows || []).reduce((total, row) => total + toNumber(row?.defect_value), 0);

export const calculateDefectSummary = ({ criticalQty, majorQty, minorQty, inspectionQty }) => {
  const totalDefectQty = toNumber(criticalQty) + toNumber(majorQty) + toNumber(minorQty);
  const inspected = toNumber(inspectionQty);
  const oqlValue = inspected > 0 ? Math.round((totalDefectQty / inspected) * 10000) / 100 : 0;
  return { totalDefectQty, oqlValue };
};

/**
 * Rows still in the form are sent as-is (new rows with id 0); saved rows the user removed
 * are sent with is_deleted = 1 so the backend soft-deletes them.
 */
export const buildInspectionDetails = (rows, savedDetails = []) => {
  const current = (rows || [])
    .filter((row) => row?.defect_category_id)
    .map((row) => ({
      id: Number(row.id) || 0,
      defect_category_id: Number(row.defect_category_id),
      defect_value: toNumber(row.defect_value),
      is_deleted: 0,
    }));

  const keptIds = new Set(current.filter((row) => row.id).map((row) => row.id));
  const removed = (savedDetails || [])
    .filter((detail) => detail?.id && !keptIds.has(Number(detail.id)))
    .map((detail) => ({
      id: Number(detail.id),
      defect_category_id: Number(detail.defect_category_id),
      defect_value: toNumber(detail.defect_value),
      is_deleted: 1,
    }));

  return [...current, ...removed];
};

/**
 * Build po_maps payload. New items have id 0, existing kept items have their id,
 * removed items have is_deleted: 1.
 */
export const buildPoMaps = (poMapIds = [], savedPoMaps = []) => {
  const selectedIds = Array.isArray(poMapIds)
    ? poMapIds.map(Number).filter(Boolean)
    : poMapIds != null && poMapIds !== ""
    ? [Number(poMapIds)].filter(Boolean)
    : [];

  const current = selectedIds.map((poMapId) => {
    const existing = (savedPoMaps || []).find((p) => Number(p?.po_map_id) === Number(poMapId));
    return {
      id: existing ? Number(existing.id) : 0,
      po_map_id: poMapId,
      is_deleted: 0,
    };
  });

  const selectedSet = new Set(selectedIds);
  const removed = (savedPoMaps || [])
    .filter((p) => p?.id && !selectedSet.has(Number(p.po_map_id)))
    .map((p) => ({
      id: Number(p.id),
      po_map_id: Number(p.po_map_id),
      is_deleted: 1,
    }));

  return [...current, ...removed];
};

export const buildFinalInspectionPayload = (values, style, savedDetails = [], savedPoMaps = []) => {
  const { totalDefectQty, oqlValue } = calculateDefectSummary({
    criticalQty: values.critical_qty,
    majorQty: values.major_qty,
    minorQty: values.minor_qty,
    inspectionQty: values.inspection_qty,
  });

  const rawPoMapIds = values.po_map_ids ?? (values.po_map_id != null ? [values.po_map_id] : []);
  const poMaps = buildPoMaps(rawPoMapIds, savedPoMaps);

  return {
    style_id: Number(style?.id),
    sales_contract_id: Number(style?.active_sales_contract_map_info?.sales_contract_id) || null,
    buying_office_id: style?.buying_office_id ?? null,
    buyer_id: style?.buyer_id ?? null,
    sub_contract_factory_id: style?.sub_contract_factory_id ?? null,
    color_id: values.color_id ?? null,
    sub_style_id: values.sub_style_id ?? null,
    po_map_id: Array.isArray(rawPoMapIds) && rawPoMapIds.length === 1 ? Number(rawPoMapIds[0]) : null,
    po_maps: poMaps,
    report_link: (values.report_link || "").trim(),
    inspection_date: formatDate(values.inspection_date),
    ship_date: formatDate(values.ship_date),
    ship_qty: toNumber(values.ship_qty),
    inspection_qty: toNumber(values.inspection_qty),
    sample_qty: toNumber(values.sample_qty),
    aql_value: toNumber(values.aql_value),
    inspection_type_id: values.inspection_type_id ?? null,
    inspection_lead_by_id: values.inspection_lead_by_id ?? null,
    inspection_status: values.inspection_status,
    critical_qty: toNumber(values.critical_qty),
    major_qty: toNumber(values.major_qty),
    minor_qty: toNumber(values.minor_qty),
    note: values.note || "",
    oql_value: oqlValue,
    total_defect_qty: totalDefectQty,
    inspection_details: buildInspectionDetails(values.defects, savedDetails),
  };
};
